import { gql, useQuery } from '@apollo/client';
import toCurrency from '@orn/shared-web/utils/toCurrency';

import type {
  AdminGetGlobalStatisticsQuery,
  AdminGetGlobalStatisticsQueryVariables,
} from './__generated__/GlobalStatistics.gql';

const ADMIN_GET_GLOBAL_STATISTICS = gql`
  query AdminGetGlobalStatistics($familyId: ID!) {
    adminGetLatestTransactionStatistics(familyId: $familyId) {
      id
      startDate
      endDate
      statsJson
    }
  }
`;

interface IGlobalStatisticsProps {
  familyId: string | null;
}

interface IGlobalStatsShape {
  count: number;
  median: number;
  percentile75: number;
  percentile90: number;
  percentile95: number;
  percentile99: number;
}

/**
 * Renders a static row of the family's global transaction stats (count + key
 * percentiles). Used as context above the priority sections in the review
 * scene; renders nothing until the mops-api stats query resolves with data.
 */
const GlobalStatistics = ({ familyId }: IGlobalStatisticsProps) => {
  const { data, loading, error } = useQuery<AdminGetGlobalStatisticsQuery, AdminGetGlobalStatisticsQueryVariables>(
    ADMIN_GET_GLOBAL_STATISTICS,
    {
      variables: { familyId: familyId ?? '' },
      skip: !familyId,
    }
  );

  if (loading || error) {
    return null;
  }

  const stats = data?.adminGetLatestTransactionStatistics?.statsJson as IGlobalStatsShape | null | undefined;
  if (!stats) {
    return null;
  }

  return (
    <section className="border-border-primary flex flex-col gap-3 border p-4">
      <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Global statistics</h2>
      <div className="grid grid-cols-6 gap-3">
        <Stat label="Count" value={stats.count.toString()} />
        <Stat label="Median" value={toCurrency(stats.median)} />
        <Stat label="75th %" value={toCurrency(stats.percentile75)} />
        <Stat label="90th %" value={toCurrency(stats.percentile90)} />
        <Stat label="95th %" value={toCurrency(stats.percentile95)} />
        <Stat label="99th %" value={toCurrency(stats.percentile99)} />
      </div>
    </section>
  );
};

interface IStatProps {
  label: string;
  value: string;
}

const Stat = ({ label, value }: IStatProps) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-fg-tertiary text-xs uppercase tracking-wide">{label}</span>
    <span className="text-fg-primary font-mono text-sm tabular-nums">{value}</span>
  </div>
);

export default GlobalStatistics;
